/**
 * Scheduling Link Utilities
 *
 * Shared helpers for working with scheduling links across apps.
 */

import type {
  SchedulingLinkPrefillDataI,
  PrefillProcessor,
} from "../interfaces/scheduling";
import {
  SCHEDULING_PROVIDERS,
  SCHEDULING_SERVICE_PROCESSORS,
  GUEST_URL_CAPABLE_DOMAINS,
} from "../constants/scheduling";

export type { SchedulingLinkPrefillDataI };
export {
  SCHEDULING_PROVIDERS,
  SCHEDULING_SERVICE_PROCESSORS,
  GUEST_URL_CAPABLE_DOMAINS,
};

const ALL_KNOWN_DOMAINS = Object.values(SCHEDULING_PROVIDERS).flatMap(
  (provider) =>
    typeof provider === "string"
      ? [provider]
      : [provider.domain, ...provider.altDomains]
);

const extractBaseDomain = (hostname: string): string | null => {
  for (const domain of ALL_KNOWN_DOMAINS) {
    if (hostname === domain || hostname.endsWith(`.${domain}`)) {
      return domain;
    }
  }

  return null;
};

const getProcessor = (url: URL): PrefillProcessor | null => {
  const baseDomain = extractBaseDomain(url.hostname);

  if (!baseDomain) {
    return null;
  }

  return SCHEDULING_SERVICE_PROCESSORS[baseDomain] || null;
};

/**
 * Ensures a URL has a protocol prefix.
 * Scheduling links are often stored without protocol (e.g. "calendly.com/someone").
 */
export const ensureSchedulingLinkProtocol = (url: string): string => {
  if (!url) {
    return url;
  }

  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }

  return `https://${url}`;
};

/**
 * True when the link's provider pre-adds a guest email via a URL query param
 * (Calendly, cal.com, Zcal). Lets the UI auto-embed the guest for these and
 * fall back to a manual "add as guest" step for every other provider, which
 * only prefills the primary booker. See GUEST_URL_CAPABLE_DOMAINS.
 */
export const schedulingLinkSupportsGuests = (
  schedulingLink: string | null | undefined
): boolean => {
  if (!schedulingLink) {
    return false;
  }

  try {
    const url = new URL(ensureSchedulingLinkProtocol(schedulingLink));
    const baseDomain = extractBaseDomain(url.hostname);

    return (
      baseDomain !== null && GUEST_URL_CAPABLE_DOMAINS.includes(baseDomain)
    );
  } catch {
    return false;
  }
};

/**
 * Adds prefill query parameters to a scheduling link based on the service provider.
 * Automatically prepends https:// if no protocol is present.
 * Returns the original URL unchanged if service is not recognized.
 */
export const prefillSchedulingLink = (
  schedulingLink: string,
  data: SchedulingLinkPrefillDataI
): string => {
  if (
    !schedulingLink ||
    (!data.name && !data.firstName && !data.email && !data.guests?.length)
  ) {
    return ensureSchedulingLinkProtocol(schedulingLink);
  }

  try {
    const normalizedLink = ensureSchedulingLinkProtocol(schedulingLink);
    const url = new URL(normalizedLink);
    const processor = getProcessor(url);

    if (!processor) {
      return normalizedLink;
    }

    return processor(url, data).toString();
  } catch {
    return ensureSchedulingLinkProtocol(schedulingLink);
  }
};
